import { theme } from '@blackhyve/common/src/AppTheme';
import AddMemberIconComponent from '@blackhyve/common/src/icons/AddMember';
import SvgBlackhyveIcon from '@blackhyve/common/src/icons/BlackhyveIcon';
import { CompanyIcon } from '@blackhyve/common/src/icons/CompanyIcon';
import RecipeIconComponent from '@blackhyve/common/src/icons/Recipe';
import SvgTaskIcon from '@blackhyve/common/src/icons/Task';
import WorkspaceIconComponent from '@blackhyve/common/src/icons/Workspace';
import { MenuOpen, Notifications } from '@mui/icons-material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import {
  Avatar,
  Box,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  ThemeProvider,
  Tooltip,
  alpha,
  createTheme,
  lighten,
  styled,
  useTheme,
} from '@mui/material';
import { Popover } from 'components/Popover';
import { RequireRole } from 'features/auth/components/RequireRole';
import {
  logout,
  selectCurrentUser,
  selectCurrentUserWorkspaceId,
} from 'features/auth/store/authSlice';
import { NotificationDrawer } from 'features/notifications/components/NotificationDrawer';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { CompanyDrawer } from './drawers/CompanyDrawer';
import { ProjectDrawer } from './drawers/ProjectDrawer';
import { RecipeDrawer } from './drawers/RecipeDrawer';
import { useWebSocket } from 'helpers/websocket';

const navTheme = createTheme({
  ...theme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#D1A8FF',
    },
    secondary: {
      main: theme.palette.secondary.main,
    },
    background: {
      paper: '#4c4c4c',
    },
  },
});

const StyledSlide = styled(Box)({ transition: 'width .3s ease' });

const COLORS = ['red', 'green', 'orange', 'blue', 'purple', 'teal'];

const drawerWith = '400px';

export const Nav = () => {
  const [drawerComponent, setDrawerComponent] = useState(undefined);
  const [drawerName, setDrawerName] = useState(undefined);
  const workspaceId = useSelector((state) => selectCurrentUserWorkspaceId(state));

  const handleToggleDrawer = (newDrawerName, newDrawerComponent) => {
    if (drawerName !== newDrawerName) {
      setDrawerComponent(newDrawerComponent);
      setDrawerName(newDrawerName);
    } else {
      setDrawerName(undefined);
    }
  };

  return (
    <ThemeProvider theme={navTheme}>
      <Box
        alignItems={'center'}
        bgcolor={'#2C2C2C'}
        display={'flex'}
        flexDirection={'column'}
        flexShrink={0}
        overflow={'auto'}
      >
        <Box
          boxSizing={'border-box'}
          component={SvgBlackhyveIcon}
          height={'auto'}
          py={2}
          width={'auto'}
        />
        <Box disablePadding component={List} width={1}>
          {/* <RequireRole exclude roles={['contractor_contact']}> */}
          <NavListItem
            icon={<WorkspaceIconComponent color={'currentColor'} />}
            selected={drawerName === 'project'}
            title={'Projects'}
            onClick={() => handleToggleDrawer('project', <ProjectDrawer />)}
          />
          {/* </RequireRole> */}
          {/* <RequireRole exclude roles={['contractor_contact']}> */}
          <NavListItem
            icon={<SvgTaskIcon color={'currentColor'} />}
            title={'Task Portfolio'}
            to={{ pathname: `/workspace/${workspaceId}/tasks/gantt` }}
          />
          {/* </RequireRole> */}
          {/* <RequireRole roles={['contractor_contact']}> */}
          {/* <NavListItem
            icon={<SvgTaskIcon color={'currentColor'} />}
            title={'My Tasks'}
            to={{ pathname: `/workspace/${workspaceId}/my-tasks` }}
          /> */}
          {/* </RequireRole> */}
          <NavListItem
            icon={<Notifications />}
            selected={drawerName === 'notifications'}
            title={'Notifications'}
            onClick={() => handleToggleDrawer('notifications', <NotificationDrawer />)}
          />
          <RequireRole roles={['contractor_contact']}>
            <NavListItem
              icon={<PendingActionsOutlinedIcon color={'currentColor'} />}
              title={'Companies Stuck Points'}
              to={{ pathname: `/workspace/${workspaceId}/stuck-points` }}
            />
          </RequireRole>
          <RequireRole exclude roles={['contractor_contact']}>
            <NavListItem
              icon={<TimelineIcon />}
              title={'Activity Feed'}
              to={{ pathname: `/workspace/${workspaceId}/activity-feeds` }}
            />
          </RequireRole>
        </Box>
        <Box disablePadding component={List} mt={'auto'} width={1}>
          <RequireRole exclude roles={['contractor_contact']}>
            <NavListItem
              icon={<RecipeIconComponent color="currentColor" />}
              selected={drawerName === 'recipe'}
              title={'Recipes'}
              onClick={() => handleToggleDrawer('recipe', <RecipeDrawer />)}
            />
          </RequireRole>
          <RequireRole exclude roles={['contractor_contact']}>
            <NavListItem
              icon={<CompanyIcon />}
              selected={drawerName === 'company'}
              title={'Companies'}
              onClick={() => handleToggleDrawer('company', <CompanyDrawer />)}
            />
          </RequireRole>
          {/* <RequireRole roles={['admin', 'super_admin', 'member']}>
            <NavListItem
              icon={<AddMemberIconComponent color="currentColor" />}
              to={{ pathname: `/workspace/${workspaceId}/users` }}
            />
          </RequireRole> */}
          <UserMenu />
        </Box>
      </Box>
      <Box display={'flex'} position={'relative'}>
        {drawerName && (
          <Tooltip arrow title={'Collapse'}>
            <Fab
              color={'primary'}
              size="small"
              sx={{
                position: 'absolute',
                right: '0',
                top: '14px',
                translate: '50% 0%',
                width: '36px',
                height: '36px',
                fontSize: '18px',
                bgcolor: (theme) => lighten(theme.palette.background.paper, 0.4),
                color: (theme) => theme.palette.text.primary,
              }}
              onClick={() => setDrawerName(undefined)}
            >
              <MenuOpen />
            </Fab>
          </Tooltip>
        )}
        <StyledSlide
          flexShrink={0}
          overflow={'hidden'}
          style={{
            width: drawerComponent !== undefined && drawerName !== undefined ? drawerWith : '0px',
          }}
        >
          <Box
            square
            component={Paper}
            display={'flex'}
            flexDirection={'column'}
            height={'100%'}
            maxWidth={'80vw'}
            position={'relative'}
            width={drawerWith}
            zIndex={1}
          >
            {drawerComponent}
          </Box>
        </StyledSlide>
      </Box>
    </ThemeProvider>
  );
};

const NavLinkListItem = ({ component, ...props }) => <ListItem component={NavLink} {...props} />;

export const NavListItem = ({ icon, children, title, TooltipProps, ...props }) => {
  const theme = useTheme();
  return (
    <Tooltip
      arrow
      disableInteractive
      placement="top"
      title={title}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
      }}
      {...TooltipProps}
    >
      <span>
        <Box
          button
          component={props.to ? NavLinkListItem : ListItem}
          style={{ justifyContent: 'center' }}
          sx={{
            '&.active': { color: theme.palette.primary.main },
            '&.active svg': { color: theme.palette.primary.main },
            '&.Mui-selected': { backgroundColor: theme.palette.background.paper },
            '& > .zeroMinWidth': { minWidth: '0px' },
          }}
          {...props}
        >
          {children ? (
            children
          ) : (
            <>
              <ListItemIcon className={'zeroMinWidth'}>{icon}</ListItemIcon>
              <Box fontSize={theme.typography.body1.fontSize} minHeight={'1.5em'} my={0.5} />
            </>
          )}
        </Box>
      </span>
    </Tooltip>
  );
};

const UserMenu = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [avatarColor, setAvatarColor] = useState(COLORS[Math.floor(Math.random() * COLORS.length)]);

  const user = useSelector((state) => selectCurrentUser(state));

  const { disconnectSocket } = useWebSocket();

  const handleLogout = () => {
    dispatch(logout()).then(() => {
      disconnectSocket();
    });
  };

  return (
    <Popover
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      trigger={
        <IconButton size="large">
          <Tooltip arrow placement="top" title={'Settings'}>
            <Avatar style={{ backgroundColor: avatarColor, color: '#fff' }} variant={'rounded'}>
              {user?.first_name[0]}
              {user?.last_name[0]}
            </Avatar>
          </Tooltip>
        </IconButton>
      }
    >
      {({ handleClose }) => (
        <MenuList>
          <MenuItem
            onClick={() => {
              navigate(`user-profile`);
              handleClose();
            }}
          >
            <ListItemIcon>
              <AccountCircleOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={'My Profile'} />
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <ExitToAppOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={'Logout'} />
          </MenuItem>
        </MenuList>
      )}
    </Popover>
  );
};
